import axios from "axios";

import {
  api,
  TokenResponse,
  setTokens,
  clearTokens,
  clearSubAccountToken,
  getAccessToken as getStoredAccessToken,
  getRefreshToken as getStoredRefreshToken,
} from "@/lib/api";

export interface LoginResponse extends TokenResponse {
  requires_2fa?: boolean;
  temp_token?: string;
  must_change_password?: boolean;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  phone?: string;
  plan_id?: string;
  billing_cycle?: "monthly" | "yearly";
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface CheckoutDataRequest {
  person_type?: string;
  cpf?: string;
  cnpj?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  billing_data?: {
    company_name?: string;
    email?: string;
    phone?: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    state?: string;
    zip_code?: string;
  };
  plan_id?: string;
  billing_cycle?: string;
  coupon_code?: string;
  total_amount?: number;
  discount_amount?: number;
  payment_method?: {
    type?: string;
    brand?: string;
    last4: string;
    exp_month?: number;
    exp_year?: number;
    holder_name?: string;
  };
}

export interface CheckoutDataResponse {
  message: string;
  user_updated: boolean;
  account_updated: boolean;
  subscription_id?: string;
  invoice_id?: string;
  payment_url?: string | null;
  pdf_url?: string | null;
}

export interface PasswordResetResponse {
  message: string;
}

export interface RegisterResponse {
  user: {
    id: string;
    email: string;
    name: string;
    show_first_access_tutorial?: boolean;
  };
  access_token: string;
  refresh_token?: string;
  token_type?: string;
  expires_in?: number;
  needs_2fa?: boolean;
}

class AuthService {
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const payload = { ...data, confirm_password: data.password };
    const response = await api.post("/auth/register", payload);
    const registerData: RegisterResponse = response.data;

    // Cadastro novo entra como sessão efêmera (sessionStorage); o usuário pode
    // optar por "Manter Conectado" num login futuro.
    setTokens(
      {
        access_token: registerData.access_token,
        refresh_token: registerData.refresh_token,
        token_type: registerData.token_type,
        expires_in: registerData.expires_in,
      },
      false,
    );

    return registerData;
  }

  async login(data: LoginRequest, rememberMe = false): Promise<LoginResponse> {
    const response = await api.post("/auth/login", { ...data, remember_me: rememberMe });
    const loginResponse: LoginResponse = response.data;

    // If 2FA is required, don't set tokens yet
    if (loginResponse.requires_2fa) {
      return loginResponse;
    }

    clearSubAccountToken();
    setTokens(loginResponse, rememberMe);

    return loginResponse;
  }

  async verify2FA(tempToken: string, code: string, rememberMe = false): Promise<TokenResponse> {
    const response = await api.post("/auth/verify-2fa", {
      temp_token: tempToken,
      code: code,
      remember_me: rememberMe,
    });
    const tokens: TokenResponse = response.data;

    clearSubAccountToken();
    setTokens(
      {
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
        token_type: tokens.token_type,
        expires_in: tokens.expires_in,
      },
      rememberMe,
    );

    return tokens;
  }

  async exchangeAdminImpersonationToken(impersonationToken: string): Promise<TokenResponse> {
    const response = await api.post("/auth/admin-impersonation/exchange", {
      token: impersonationToken,
    });
    const tokens: TokenResponse = response.data;

    // Impersonação de suporte é sempre sessão efêmera (sessionStorage).
    clearSubAccountToken();
    setTokens(
      {
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
        token_type: tokens.token_type,
        expires_in: tokens.expires_in,
      },
      false,
    );

    return tokens;
  }

  async logout(): Promise<void> {
    const accessToken = getStoredAccessToken();
    const refreshToken = getStoredRefreshToken();

    // Limpeza local imediata: encerrar a sessão (e a navegação de saída que o
    // chamador faz em seguida) não espera a rede.
    // clearTokens() limpa access/refresh/token_expires_at/last_activity dos dois storages.
    clearTokens();
    clearSubAccountToken();

    if (!accessToken || !refreshToken) {
      return;
    }

    // Revogação server-side em segundo plano, com axios SEM interceptors: após a
    // limpeza local o interceptor já não teria o header de autorização, e o 401
    // resultante dispararia o redirect duro para /login por cima da navegação em
    // curso (ex.: recuperação de senha).
    void axios
      .post(`${api.defaults.baseURL}/auth/logout`, null, {
        params: { refresh_token: refreshToken },
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .catch((error: unknown) => {
        console.error("Erro ao fazer logout:", error);
      });
  }

  async refreshToken(): Promise<boolean> {
    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await api.post("/auth/refresh", {
        refresh_token: refreshToken,
      });

      // Sem flag → preserva o storage ativo da sessão e grava token_expires_at.
      const tokens = response.data;
      setTokens({
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token || refreshToken,
        token_type: tokens.token_type,
        expires_in: tokens.expires_in,
      });

      return true;
    } catch (error) {
      console.error("Failed to refresh token:", error);
      return false;
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post("/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: newPassword,
    });
  }

  async requestPasswordReset(email: string): Promise<PasswordResetResponse> {
    const response = await api.post("/auth/password/reset-request", { email });
    return response.data;
  }

  async validatePasswordResetToken(token: string): Promise<void> {
    await api.get("/auth/password/reset-validate", {
      params: { token },
    });
  }

  async resetPassword(
    token: string,
    newPassword: string,
    confirmPassword: string,
  ): Promise<PasswordResetResponse> {
    const response = await api.post("/auth/password/reset-confirm", {
      token,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
    return response.data;
  }

  async updateCheckoutData(data: CheckoutDataRequest): Promise<CheckoutDataResponse> {
    const response = await api.post("/auth/checkout-data", data);
    return response.data;
  }

  isAuthenticated(): boolean {
    return !!getStoredAccessToken();
  }

  getAccessToken(): string | null {
    return getStoredAccessToken();
  }
}

export default new AuthService();
